import React, { useState, useEffect } from "react";
import axios from 'axios';
import params from '../config/Params';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom'; 
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const WebEarning = () => {
  const { token } = useParams();
  const [earnings, setEarnings] = useState([]);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [weeklyEarnings, setWeeklyEarnings] = useState(0);
  const [monthlyEarnings, setMonthlyEarnings] = useState(0);
  const [expandedRows, setExpandedRows] = useState({});
  const [filters, setFilters] = useState({
    dateFrom: "",
    dateTo: "",
  });

  const data = [
    { month: "Jan", earnings: 4000 },
    { month: "Feb", earnings: 3000 },
    { month: "Mar", earnings: 5000 },
    { month: "Apr", earnings: 2500 },
    { month: "May", earnings: 6000 },
    { month: "Jun", earnings: 4500 },
    { month: "Jul", earnings: 7000 },
    { month: "Aug", earnings: 4800 },
    { month: "Sep", earnings: 5200 },
    { month: "Oct", earnings: 6100 },
    { month: "Nov", earnings: 4900 },
    { month: "Dec", earnings: 7500 },
  ];

  useEffect(() => {
    fetchEarnings();
  }, [filters]);

  const fetchEarnings = async () => {
    let formData = new FormData();
    formData.append('token', token);
    formData.append('date_from', filters.dateFrom || '');
    formData.append('date_to', filters.dateTo || '');

    try {
      const response = await axios.post(
        `${params.baseURL}${params.endpoints.getEarnings}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data.status === 'success') {
        const data = response.data.data;

        const total = data.reduce((sum, item) => sum + parseFloat(item.total_earning || 0), 0);

        const week = data.reduce((sum, item) => {
          if (isThisWeek(item.booking_date)) {
            return sum + parseFloat(item.total_earning || 0);
          }
          return sum;
        }, 0);

        const month = data.reduce((sum, item) => {
          if (isThisMonth(item.booking_date)) {
            return sum + parseFloat(item.total_earning || 0);
          }
          return sum;
        }, 0);

        setEarnings(data);
        setTotalEarnings(total);
        setWeeklyEarnings(week);
        setMonthlyEarnings(month);
      } else {
        toast.error('Unable to fetch earnings. Try again.');
      }
    } catch (error) {
      console.error('Error fetching earnings:', error);
      toast.error('An error occurred while fetching earnings.');
    }
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value,
    });
  };

  /*const handleSearch = () => {
    fetchEarnings();
  };*/

  const isThisWeek = (date) => {
    const today = new Date();
    const bookingDate = new Date(date);
    const startOfWeek = new Date(today.setDate(today.getDate() - today.getDay()));
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    return bookingDate >= startOfWeek && bookingDate <= endOfWeek;
  };

  const isThisMonth = (date) => {
    const today = new Date();
    const bookingDate = new Date(date);
    return bookingDate.getMonth() === today.getMonth() && bookingDate.getFullYear() === today.getFullYear();
  };

  const calculateDaysAgo = (dateString) => {
    if (!dateString) return "-";
    const today = new Date();
    const date = new Date(dateString);
    const diffTime = Math.abs(today - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return `${diffDays} days`;
  };

  const toggleRow = (index) => {
    setExpandedRows((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const exportToExcel = () => {
    if (earnings.length === 0) {
      toast.info("No earnings data to export.");
      return;
    }

    const formattedData = earnings.map((item) => ({
      "Property Name": item.property_name || "-",
      "Booking ID": item.booking_id || "-",
      "Amount": parseFloat(item.amount || 0).toFixed(2),
      "Guest Charge": parseFloat(item.guest_charge || 0).toFixed(2),
      "Host Charge": parseFloat(item.host_charge || 0).toFixed(2),
      "Affiliate": parseFloat(item.affiliate || 0).toFixed(2),
      "Coupons": parseFloat(item.coupons || 0).toFixed(2),
      "GST": parseFloat(item.gst || 0).toFixed(2),
      "Total Earning": parseFloat(item.total_earning || 0).toFixed(2),
      "Subscription": item.subscription_active ? "Active" : "Inactive",
      "Booking Date": item.booking_date || "-",
      "Days Ago": calculateDaysAgo(item.booking_date),
      "Status": item.status || "-"
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Earnings");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, "earnings_report.xlsx");
  };

  return (
    <div>
      <Helmet>
        <title>Earnings</title>
        <meta name="description" content="" />
      </Helmet>

      <style>
        {`
          #main {
            margin-top:0;
            padding: 20px 12px;
          }
        `}
      </style>

      <div className="earning-section">

        <section className="mb-4">
          <div className="te-row">
            <div className="te-col-md-12">
              <div className="title-box">
                <h4 style={{fontSize:'30px'}}>Your total earning is <span style={{color:'#5500ff'}}>{totalEarnings.toFixed(2)} INR</span></h4>
              </div>
            </div>
          </div>
        </section>
        <section class="earning-glass" id="total-earning">
            <div class="balance-card mb-0">
              <div className="row">
                <div className="col-md-6">
                  <div class="balance-stats m-0">
                    <div class="stat">
                        <h2 class="earning-label">Monthly</h2>
                        <span id="totalIncome" class="income">INR {monthlyEarnings.toFixed(2)}</span>
                    </div>
                    <div class="stat">
                        <h2 class="earning-label">Weekly</h2>
                        <span id="totalIncome" class="income">INR {weeklyEarnings.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
        </section>

        <div className="mt-4">
          <h4 style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: '1rem' }}>
            Monthly Earnings - {new Date().getFullYear()}
          </h4>

          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={data} margin={{ top: 5, right: 5, left: 5, bottom: 5 }} barSize={20}>
              <XAxis dataKey="month" scale="point" padding={{ left: 10, right: 10 }} />
              <YAxis />
              <Tooltip />
              <Legend />
              <CartesianGrid strokeDasharray="3 3" />
              <Bar dataKey="earnings" fill="#5500ff" background={{ fill: '#eee' }} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-3 mb-3">
          <button className="btn btn-black w-100" onClick={exportToExcel}>
            Download Report
          </button>
        </div>

        <div class="quick-actions earning-glass">
            <div>
                <label htmlFor="dateFrom" class="form-label">From Date</label>
                <div className='iwtb'>
                    <img src={'/assets/img/icons/calendar_black.png'} alt="Profile" className="form-icon" />
                    <input
                        type="date"
                        name="dateFrom"
                        id="dateFrom"
                        class="glass-input"
                        value={filters.dateFrom}
                        onChange={handleFilterChange}
                    />
                </div>
            </div>
            <div>
                <label htmlFor="dateTo" class="form-label">To Date</label>
                <div className='iwtb'>
                    <img src={'/assets/img/icons/calendar_black.png'} alt="Profile" className="form-icon" />
                    <input
                        type="date"
                        name="dateTo"
                        id="dateTo"
                        class="glass-input"
                        value={filters.dateTo}
                        onChange={handleFilterChange}
                    />
                </div>
            </div>
            {/*<button id="addTransaction" class="btn-primary" onClick={handleSearch}>Search</button>*/}
        </div>

        <section className="earning-glass mt-4" id="earnings-table">
          <div className="web-earning-box">
            {earnings.length > 0 ? (
              earnings.map((earning, index) => (
                <div className="data-row" key={index}>
                  <div className="data-container">
                    <div className="title-cell" style={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                      {earning.property_name
                        ? earning.property_name.length > 15
                          ? earning.property_name.substring(0, 20) + "..."
                          : earning.property_name
                        : "-"}
                      <span>
                        <img
                          src={earning.subscription_active ? '/assets/img/active_subscription.svg' : '/assets/img/inactive_subscription.svg'}
                          alt="Subscription Status"
                          className="pointer-icon tb"
                        />
                      </span>
                    </div>

                    <div className="data-cell">
                      <p>Booking ID:</p> 
                      <span>
                        <img src="/assets/img/icons/common_point_icon.png" alt="pointer" className="pointer-icon tb" />
                        {earning.booking_id || "-"}
                      </span>
                    </div>

                    {!expandedRows[index] && (
                      <div className="data-cell">
                        <p>Total Earning:</p>
                        <span>
                          <img src="/assets/img/icons/common_point_icon.png" alt="pointer" className="pointer-icon tb" />
                          INR {parseFloat(earning.total_earning || 0).toFixed(2)}
                        </span>
                      </div>
                    )}

                    {expandedRows[index] && (
                      <div style={{ marginTop: '10px' }}>
                        <div className="data-cell">
                          <p>Amount:</p> 
                          <span>
                            <img src="/assets/img/icons/common_point_icon.png" alt="pointer" className="pointer-icon tb" />
                            INR {parseFloat(earning.amount || 0).toFixed(2)}
                          </span>
                        </div>
                        <div className="data-cell">
                          <p>Guest:</p> 
                          <span>
                            <img src="/assets/img/icons/common_point_icon.png" alt="pointer" className="pointer-icon tb" />
                            INR {parseFloat(earning.guest_charge || 0).toFixed(2)}
                          </span>
                        </div>
                        <div className="data-cell">
                          <p>Host:</p> 
                          <span>
                            <img src="/assets/img/icons/common_point_icon.png" alt="pointer" className="pointer-icon tb" />
                            INR {parseFloat(earning.host_charge || 0).toFixed(2)}
                          </span>
                        </div>
                        <div className="data-cell">
                          <p>Affiliate:</p> 
                          <span>
                            <img src="/assets/img/icons/common_point_icon.png" alt="pointer" className="pointer-icon tb" />
                            INR {parseFloat(earning.affiliate || 0).toFixed(2)}
                          </span>
                        </div>
                        <div className="data-cell">
                          <p>Subscription:</p> 
                          <span>
                            {earning.subscription_active ? (
                              <span className="badge bg-success">Active</span>
                            ) : (
                              <span className="badge bg-danger">Inactive</span>
                            )}
                          </span>
                        </div>
                        <div className="data-cell">
                          <p>Coupons:</p> 
                          <span>
                            <img src="/assets/img/icons/common_point_icon.png" alt="pointer" className="pointer-icon tb" />
                            INR {parseFloat(earning.coupons || 0).toFixed(2)}
                          </span>
                        </div>
                        <div className="data-cell">
                          <p>GST:</p> 
                          <span>
                            <img src="/assets/img/icons/common_point_icon.png" alt="pointer" className="pointer-icon tb" />
                            INR {parseFloat(earning.gst || 0).toFixed(2)}
                          </span>
                        </div>

                        <div className="data-cell">
                          <p>Total Earning:</p>
                          <span>
                            <img src="/assets/img/icons/common_point_icon.png" alt="pointer" className="pointer-icon tb" />
                            INR {parseFloat(earning.total_earning || 0).toFixed(2)}
                          </span>
                        </div>

                        <div className="data-cell">
                          <p>Days Ago:</p> 
                          <span>
                            <img src="/assets/img/icons/common_point_icon.png" alt="pointer" className="pointer-icon tb" />
                            {calculateDaysAgo(earning.booking_date)}
                          </span>
                        </div>
                        <div className="data-cell">
                          <p>Status:</p> 
                          <span>
                            <img src="/assets/img/icons/common_point_icon.png" alt="pointer" className="pointer-icon tb" />
                            {earning.status || "-"}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  <button onClick={() => toggleRow(index)} className="engtbtn">
                    {expandedRows[index] ? "Close" : "Total Breakup"}
                  </button>
                </div>
              ))
            ) : (
              <div className="table-row">
                <div style={{ flex: '1 0 100%' }}>
                  No earnings found.
                </div>
              </div>
            )}
          </div>
        </section>

      </div>

    </div>
  );
};

export default WebEarning;
